// KEEP IN SYNC: server/src/services/email/blocks.ts manually ports the block
// types and the heading/paragraph/list/table/billingTotals/bankDetails
// renderers + parseBlocksFromHtml from this file (the client/server split
// means it can't import this module directly). Any change to a type here, a
// renderer's output HTML, or EMAIL_STYLES must be mirrored there or the
// Sales RM composer / sent emails will silently render admin blocks
// differently than this file's own admin-editor preview.
export type EmailBlockType =
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'table'
  | 'bankDetails'
  | 'productsTable'
  | 'billingTotals'
  | 'tcLinks'
  | 'additionalArns'
  | 'installments'
  | 'raw';

interface BaseBlock {
  id: string;
  type: EmailBlockType;
}

export interface HeadingBlock extends BaseBlock {
  type: 'heading';
  title: string;
  meta: string;
}

export interface ParagraphBlock extends BaseBlock {
  type: 'paragraph';
  text: string;
  bold: boolean;
  italic?: boolean;
}

export interface ListBlock extends BaseBlock {
  type: 'list';
  items: string[];
}

export interface TableBlock extends BaseBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}

export interface BankDetailsBlock extends BaseBlock {
  type: 'bankDetails';
}

export interface ProductsTableBlock extends BaseBlock {
  type: 'productsTable';
}

export interface BillingTotalsBlock extends BaseBlock {
  type: 'billingTotals';
  showDiscount: boolean;
}

// Prompt line + one "Open and Review the Terms & Condition - <API|MFTOOLS|All
// Products>" button per document the lead's product selection requires. The
// buttons themselves are minted server-side on a real send (see
// tc.service.ts#renderTcButtonsHtml); only the prompt is editable here.
export interface TcLinksBlock extends BaseBlock {
  type: 'tcLinks';
  prompt: string;
}

export interface AdditionalArnsBlock extends BaseBlock {
  type: 'additionalArns';
}

export interface InstallmentsBlock extends BaseBlock {
  type: 'installments';
}

export interface RawBlock extends BaseBlock {
  type: 'raw';
  html: string;
}

export type EmailBlock =
  | HeadingBlock
  | ParagraphBlock
  | ListBlock
  | TableBlock
  | BankDetailsBlock
  | ProductsTableBlock
  | BillingTotalsBlock
  | TcLinksBlock
  | AdditionalArnsBlock
  | InstallmentsBlock
  | RawBlock;

export const BLOCK_TYPE_LABELS: Record<EmailBlockType, string> = {
  heading: 'Heading Bar',
  paragraph: 'Paragraph',
  list: 'Bullet List',
  table: 'Table',
  bankDetails: 'Bank Details',
  productsTable: 'Products Table',
  billingTotals: 'Billing Totals',
  tcLinks: 'T&C Buttons',
  additionalArns: 'Additional ARNs Table',
  installments: 'Installments Table',
  raw: 'Raw HTML',
};

// Default prompt for a new T&C block, matching the wording used on the seeded
// quotation templates.
export const TC_PROMPT_DEFAULT = 'Please review and accept the applicable Terms & Conditions:';

// Single source of truth for the brand look of every email.
export const EMAIL_STYLES = {
  brand: '#067FFA',
  ink: '#1A1A18',
  line: '#C8CCC2',
  tableHeaderFill: '#D9E1F2',
  cream: '#F6F1E5',
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const headingHtml = (block: HeadingBlock): string => `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:16px;">
  <tr>
    <td style="border-bottom:3px solid ${EMAIL_STYLES.brand}; padding-bottom:10px;">
      <span style="font-size:18px; font-weight:700; color:${EMAIL_STYLES.ink};">${escapeHtml(block.title)}</span><br>
      <span style="font-size:12px; color:${EMAIL_STYLES.ink}; opacity:0.65;">${escapeHtml(block.meta)}</span>
    </td>
  </tr>
</table>`;

const paragraphHtml = (block: ParagraphBlock): string => {
  const styles = [
    'margin:0 0 12px',
    `color:${EMAIL_STYLES.ink}`,
    'font-size:14px',
    'line-height:1.5',
    ...(block.bold ? ['font-weight:700'] : []),
    ...(block.italic ? ['font-style:italic'] : []),
  ].join('; ');
  return `<p style="${styles}">${escapeHtml(block.text)}</p>`;
};

const listHtml = (block: ListBlock): string =>
  `<p style="margin:0 0 12px; color:${EMAIL_STYLES.ink}; font-size:14px; line-height:1.6;">${block.items.map((item) => `&bull; ${escapeHtml(item)}`).join('<br>')}</p>`;

// table-layout:fixed + a <colgroup> setting the first column's width keeps
// every table in an email sharing the same column proportions. cellpadding/
// cellspacing attributes are avoided in favour of CSS padding - mixing them
// with border-collapse produced double-border artifacts at cell intersections.
const FIRST_COL_WIDTH = '38%';
const TABLE_OPEN_STYLE = `border-collapse:collapse; table-layout:fixed; margin:14px 0; font-size:13px; color:${EMAIL_STYLES.ink};`;
const CELL_STYLE = `border:1px solid ${EMAIL_STYLES.line}; padding:8px; word-wrap:break-word;`;
const colgroup = (columnCount: number): string =>
  `<colgroup><col style="width:${FIRST_COL_WIDTH}">${'<col>'.repeat(Math.max(columnCount - 1, 0))}</colgroup>`;

const tableHtml = (block: TableBlock): string => `
<table role="presentation" width="100%" style="${TABLE_OPEN_STYLE}">
  ${colgroup(block.headers.length)}
  <tr>${block.headers.map((h) => `<th style="text-align:left; background:${EMAIL_STYLES.tableHeaderFill}; font-weight:700; ${CELL_STYLE}">${escapeHtml(h)}</th>`).join('')}</tr>
  ${block.rows.map((row) => `<tr>${row.map((cell) => `<td style="${CELL_STYLE}">${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}
</table>`;

// No top margin - this block always follows the products table directly.
const BILLING_TOTALS_STYLE = `border-collapse:collapse; table-layout:fixed; margin:0 0 14px; font-size:13px; color:${EMAIL_STYLES.ink};`;

const billingTotalsHtml = (block: BillingTotalsBlock): string => {
  const boldCell = (value: string): string =>
    `<td style="${CELL_STYLE}; font-weight:700">${value}</td>`;
  // Per-product discounts are itemised in the products table's Net Amount
  // column (ADR-019/020), so Sub-Total here is already net - no Discount row.
  const rows = block.showDiscount
    ? [
        `<tr><td style="${CELL_STYLE}">Sub-Total</td><td style="${CELL_STYLE}">{{discounted_subtotal}}</td></tr>`,
        `<tr><td style="${CELL_STYLE}">GST @ {{gst_percentage}}%</td><td style="${CELL_STYLE}">{{gst_amount}}</td></tr>`,
        `<tr>${boldCell('Total Payable')}${boldCell('{{total_amount}}')}</tr>`,
      ]
    : [`<tr>${boldCell('Total Payable')}${boldCell('{{total_amount}}')}</tr>`];
  return `
<table role="presentation" width="100%" style="${BILLING_TOTALS_STYLE}">
  ${colgroup(2)}
  ${rows.join('\n  ')}
</table>`;
};

const bankDetailsHtml = (): string => `
<p style="font-weight:700; margin:18px 0 6px; color:${EMAIL_STYLES.ink};">Bank Details</p>
<p style="margin:0 0 4px; color:${EMAIL_STYLES.ink};">For your convenience, our bank account and UPI details are provided below:</p>
<table role="presentation" width="100%" style="${TABLE_OPEN_STYLE}">
  ${colgroup(2)}
  <tr>${['Particulars', 'Details'].map((h) => `<th style="text-align:left; background:${EMAIL_STYLES.tableHeaderFill}; font-weight:700; ${CELL_STYLE}">${h}</th>`).join('')}</tr>
  ${[
    ['Account Name', '{{bank_account_name}}'],
    ['Bank Name', '{{bank_name}}'],
    ['Account Number', '{{bank_account_number}}'],
    ['IFSC Code', '{{bank_ifsc_code}}'],
    ['UPI ID', '{{bank_upi_id}}'],
    ['RazorPay Payment Link', '{{razorpay_link}}'],
  ]
    .map(
      ([label, value]) =>
        `<tr><td style="${CELL_STYLE}">${label}</td><td style="${CELL_STYLE}">${value}</td></tr>`,
    )
    .join('')}
</table>
<p style="font-size:12px; color:${EMAIL_STYLES.ink}; opacity:0.7;">Note: Payments must be made from the bank account of the subscribing individual or business entity. Third-party payments are not accepted.</p>`;

// The prompt is authored text (escaped); the buttons arrive as the pre-rendered,
// trusted {{tc_buttons_html}} token, resolved only at send time.
//
// Wrapped in an EB_IF section keyed on that token. The other data-driven blocks
// hide themselves by rendering '' (their own heading lives inside the same
// conditional), but this one cannot: the prompt is authored template text, not
// server output, so nothing downstream could tell that the buttons underneath it
// came back empty. That happens on any resend to a lead who has already accepted
// every applicable document - which would otherwise ship a bold "Please review
// and accept..." with nothing under it. email.service.ts#sendEmail resolves the
// section against the real token values; see resolveConditionalSections.
export const conditionalSection = (tokenKey: string, html: string): string =>
  `<!--EB_IF:${tokenKey}-->${html}<!--EB_ENDIF-->`;

/** Defuses both EB_IF and EB_ENDIF inside authored HTML. See the `raw` case. */
export const neutraliseMarkers = (html: string): string =>
  html
    .replace(/<!--EB_IF:(\w+)-->/g, '<!-- EB_IF:$1 -->')
    .split('<!--EB_ENDIF-->')
    .join('<!-- EB_ENDIF -->');

const tcLinksHtml = (block: TcLinksBlock): string =>
  conditionalSection(
    'tc_buttons_html',
    `
<p style="margin:18px 0 6px; font-weight:700; color:${EMAIL_STYLES.ink}; font-size:14px; line-height:1.5;">${escapeHtml(block.prompt)}</p>
<p style="margin:0 0 14px;">{{tc_buttons_html}}</p>`,
  );

export const renderBlockToHtml = (block: EmailBlock): string => {
  switch (block.type) {
    case 'heading':
      return headingHtml(block);
    case 'paragraph':
      return paragraphHtml(block);
    case 'list':
      return listHtml(block);
    case 'table':
      return tableHtml(block);
    case 'bankDetails':
      return bankDetailsHtml();
    case 'productsTable':
      return '{{products_table}}';
    case 'billingTotals':
      return billingTotalsHtml(block);
    case 'tcLinks':
      return tcLinksHtml(block);
    case 'additionalArns':
      return '{{additional_arns_table}}';
    case 'installments':
      return '{{installments_table}}';
    // Neutralised, not emitted verbatim - see the matching note in
    // server/src/services/email/blocks.ts. BOTH halves of the marker pair are
    // hazards inside authored HTML: a stray EB_ENDIF closes an enclosing section
    // early, a stray EB_IF opener captures the wrong key and leaves the section
    // unresolved.
    case 'raw':
      return neutraliseMarkers(block.html);
    default:
      return '';
  }
};

// The rendered HTML is prefixed with the source-of-truth block data as an HTML
// comment so re-opening the template restores the editable blocks losslessly
// instead of trying to parse arbitrary HTML back into structured fields. The
// JSON is base64-encoded before being embedded - block content (e.g. a "raw"
// block's HTML) can contain a literal "-->", which would otherwise collide
// with the marker's own closing delimiter and truncate the JSON on parse.
// Base64's alphabet has no "-" or ">", so that collision can't happen.
const MARKER_PREFIX = '<!--EB_BLOCKS:';
const MARKER_SUFFIX = '-->';

const encodeBlocks = (blocks: EmailBlock[]): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(blocks));
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const decodeBlocks = (encoded: string): unknown => {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

export const renderBlocksToHtml = (blocks: EmailBlock[]): string => {
  const marker = `${MARKER_PREFIX}${encodeBlocks(blocks)}${MARKER_SUFFIX}`;
  const rendered = blocks.map(renderBlockToHtml).join('\n');
  return `${marker}\n${rendered}`;
};

// Fallback for the plain-text body when the user leaves that field blank -
// the server requires a non-empty bodyText, so this strips tags/markup from
// the rendered HTML instead of forcing every save through a separate field.
export const htmlToPlainText = (html: string): string =>
  html
    .replace(/<!--EB_BLOCKS:[\s\S]*?-->/, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|tr|table|div|li)>/gi, '\n')
    .replace(/&bull;/gi, '*')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const parseBlocksFromHtml = (html: string): EmailBlock[] | null => {
  if (!html.startsWith(MARKER_PREFIX)) return null;
  const end = html.indexOf(MARKER_SUFFIX);
  if (end === -1) return null;
  const raw = html.slice(MARKER_PREFIX.length, end);
  try {
    // Current format: base64-encoded JSON (collision-safe against --> in content).
    const parsed = decodeBlocks(raw);
    return Array.isArray(parsed) ? (parsed as EmailBlock[]) : null;
  } catch {
    // Legacy format: plain JSON.stringify (used by seedEmailTemplates.ts before
    // the base64 migration). Fall back so old templates still open in the editor.
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as EmailBlock[]) : null;
    } catch {
      return null;
    }
  }
};

export const createBlock = (type: EmailBlockType): EmailBlock => {
  const id = crypto.randomUUID();
  switch (type) {
    case 'heading':
      return {
        id,
        type,
        title: 'Section Title',
        meta: 'ARN: {{arn}} | {{company_name}} | Ref: {{reference_number}}',
      };
    case 'paragraph':
      return {
        id,
        type,
        text: 'Dear {{client_name}},',
        bold: false,
      };
    case 'list':
      return { id, type, items: ['Step one', 'Step two'] };
    case 'table':
      return {
        id,
        type,
        headers: ['Column 1', 'Column 2'],
        rows: [['', '']],
      };
    case 'bankDetails':
      return { id, type };
    case 'productsTable':
      return { id, type };
    case 'billingTotals':
      return { id, type, showDiscount: false };
    case 'tcLinks':
      return { id, type, prompt: TC_PROMPT_DEFAULT };
    case 'additionalArns':
      return { id, type };
    case 'installments':
      return { id, type };
    case 'raw':
    default:
      return { id, type: 'raw', html: '' };
  }
};
